import { useContext, useState, useMemo, useEffect } from 'react';
import { getWaterFallChartData } from 'components/pages/GrowthMarginDrivers/hooks/growthMarginDataUtils';
import { FilterContextData } from 'contexts/FilterContext';
import { useQuery } from '@tanstack/react-query';
import fetchProfitPoolData from 'components/pages/ProfitPoolOverview/apiUtils';
import { isEmpty, startCase } from 'lodash';
import { useGrowthMarginBridgesContext } from '../GrowthMarginBridgesContext';
import { formatNumWithKMBAlt2 } from 'utils/helperMethods';

const useDecompositionWaterfallData = () => {
    const { selectedBridgesKPI } = useGrowthMarginBridgesContext();

    const [decompositionWaterfallData, setDecompositionWaterfallData] = useState<any[]>([]);
    const [totalMix, setTotalMix] = useState<number>(0);
    const isWaterfallChartEnabled = useMemo(() => selectedBridgesKPI === 'mix_impact', [selectedBridgesKPI]);

    const {
        idForRestOfComponent,
        timePeriodSelection,
        singleSelectedTier,
        allDimAndAttributeTypeList,
        timePeriodLoading,
    } = useContext(FilterContextData);

    // Get WaterFallChartData

    const {
        data: attributeData,
        isPending: isAttributeDataPending,
        isError: isAttributeDataError,
        isSuccess: isAttributeDataSuccess,
        isFetching: isAttributeDataFetching,
    } = useQuery({
        queryKey: [
            'decomposition-waterfall-infi',
            singleSelectedTier,
            idForRestOfComponent,
            timePeriodSelection,
            selectedBridgesKPI,
        ],
        queryFn: ({ signal }) => {
            return fetchProfitPoolData({
                kpi: 'mix_impact',
                tierList: singleSelectedTier,
                yearAgo: false,
                groupedByList: [],
                dimIdsFilter: idForRestOfComponent,
                frequency: 'M',
                timePeriod: JSON.parse(timePeriodSelection),
                signal,
            });
        },
        enabled: Boolean(
            allDimAndAttributeTypeList &&
                'loading' in allDimAndAttributeTypeList &&
                !timePeriodLoading &&
                timePeriodSelection &&
                selectedBridgesKPI &&
                isWaterfallChartEnabled,
        ),
    });

    useEffect(() => {
        if (!isAttributeDataPending && attributeData) {
            const allData = attributeData?.data[0];
            const finalChartData = [];
            let _totalMix = 0;

            if (!isEmpty(allData)) {
                for (const [key, value] of Object.entries(allData?.kpi_values)) {
                    _totalMix += Number(value);
                    finalChartData.push({
                        kpiKey: key,
                        kpiValue: value,
                        attributeName: startCase(key),
                        [key]: value,
                        tier: allData.tier,
                    });
                }
                setDecompositionWaterfallData(finalChartData);
                setTotalMix(_totalMix);
            } else {
                setDecompositionWaterfallData([]);
                setTotalMix(0);
            }
        }
    }, [isAttributeDataPending, attributeData]);

    // Memoize waterfall chart data to avoid recalculating multiple times
    const waterfallChartResult = useMemo(() => {
        return getWaterFallChartData(decompositionWaterfallData, 0, totalMix, 'kpiValue');
    }, [decompositionWaterfallData, totalMix]);

    // Prepare Excel export data from the original data (before waterfall transform)
    const excelExportData = useMemo(() => {
        if (!decompositionWaterfallData?.length) {
            return { headers: [], data: [] };
        }

        const headers = ['Name', 'Value', 'Trend'];

        const data = [];

        decompositionWaterfallData.map((item: any) => {
            data.push([
                item.attributeName || '',
                formatNumWithKMBAlt2(item.kpiValue, 2, 2) || 0,
                Number(item.kpiValue) >= 0 ? 'positive' : 'negative',
            ]);
        });

        data.push(['Total Mix', formatNumWithKMBAlt2(totalMix, 2, 2) || 0, '', totalMix > 0 ? 'positive' : 'negative']);

        return { headers, data };
    }, [decompositionWaterfallData]);

    return {
        data: decompositionWaterfallData,
        totalMix: totalMix,
        waterFallChartData: waterfallChartResult?.chartData,
        min: waterfallChartResult?.min,
        max: waterfallChartResult?.max,
        excelExportData,
        isPending: isAttributeDataPending,
        isFetching: isAttributeDataFetching,
        isError: isAttributeDataError,
        isSuccess: isAttributeDataSuccess,
    };
};

export default useDecompositionWaterfallData;
